import { useNavigate } from 'react-router';
import useNotifications from '../../hooks/useNotifications/useNotifications';
import { useCallback, useState } from 'react';
import PageContainer from '../layout/PageContainer';
import { validate as validateContact, } from '../../models/Contact';
import { FormFieldValue } from '../forms/FormFieldValue';
import ContactForm, { ContactFormState } from './ContactForm';
import { useMutation } from '@apollo/client/react';
import { ADD_CONTACT_MUTATION } from '../../mutations/ContactMutations';
import { GET_CONTACTS_QUERY } from '../../queries/ContactQueries';

const INITIAL_FORM_VALUES: Partial<ContactFormState['values']> = {
  name: '',
  email: '',
  phone: ''
};

export default function ContactCreate() {
  const navigate = useNavigate();

  const notifications = useNotifications();

  const [createContact] = useMutation(
    ADD_CONTACT_MUTATION, {
    refetchQueries: [{ query: GET_CONTACTS_QUERY }],
  });

  const [formState, setFormState] = useState<ContactFormState>(() => ({
    values: INITIAL_FORM_VALUES,
    errors: {},
  }));

  const formValues = formState.values;
  const formErrors = formState.errors;

  const setFormValues = useCallback(
    (newFormValues: Partial<ContactFormState['values']>) => {
      setFormState((previousState) => ({
        ...previousState,
        values: newFormValues,
      }));
    },
    [],
  );

  const setFormErrors = useCallback(
    (newFormErrors: Partial<ContactFormState['errors']>) => {
      setFormState((previousState) => ({
        ...previousState,
        errors: newFormErrors,
      }));
    },
    [],
  );

  const handleFormFieldChange = useCallback(
    (name: keyof ContactFormState['values'], value: FormFieldValue) => {
      const validateField = async (values: Partial<ContactFormState['values']>) => {
        const { issues } = validateContact(values);
        setFormErrors({
          ...formErrors,
          [name]: issues?.find((issue) => issue.path?.[0] === name)?.message,
        });
      };

      const newFormValues = { ...formValues, [name]: value };

      setFormValues(newFormValues);
      validateField(newFormValues);
    },
    [formValues, formErrors, setFormErrors, setFormValues],
  );

  const handleFormReset = useCallback(() => {
    setFormValues(INITIAL_FORM_VALUES);
  }, [setFormValues]);

  const handleFormSubmit = useCallback(async () => {
    const { issues } = validateContact(formValues);

    if (issues && issues.length > 0) {
      setFormErrors(
        Object.fromEntries(issues.map((issue) => [issue.path?.[0], issue.message])),
      );
      return;
    }

    setFormErrors({});

    try {
      const { data: result } = await createContact({
        variables: {
          input: {
            id: 0,
            name: formValues.name || '',
            email: formValues.email || '',
            phone: formValues.phone || ''
          }
        }
      });

      if (result?.added) {
        notifications.show('Contact created successfully.', {
          severity: 'success',
          autoHideDuration: 3000,
        });

        navigate('/dashboard/contacts');
      } else {
        notifications.show(
          'Failed to create contact. Reason: Unknown.',
          {
            severity: 'error',
            autoHideDuration: 3000,
          },
        );
      }
    } catch (createError) {
      notifications.show(
        `Failed to create contact. Reason: ${(createError as Error).message}`,
        {
          severity: 'error',
          autoHideDuration: 3000,
        },
      );
      throw createError;
    }
  }, [createContact, formValues, navigate, notifications, setFormErrors]);

  return (
    <PageContainer
      title="New Contact"
      breadcrumbs={[{ title: 'Contacts', path: '/dashboard/contacts' }, { title: 'New' }]}
    >
      <ContactForm
        formState={formState}
        onFieldChange={handleFormFieldChange}
        onSubmit={handleFormSubmit}
        onReset={handleFormReset}
        submitButtonLabel="Create"
      />
    </PageContainer>
  );
}
